import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { Button } from '@/components/shadcn/button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useRestClient } from '@/contexts/QueryProvider'
import { IconBg } from '@/components/ui/IconBg'
import { IconCheckMarkOutline } from '@icons/IconCheckMarkOutline'
import { toast } from 'sonner'

export function ReopenOrDeleteActions({
  channelId,
  threadId,
  invalidateThreadAndLogs,
}: {
  channelId?: number
  threadId?: number
  invalidateThreadAndLogs: () => void
}) {
  const { workspaceId, appId } = useAppContext()

  const { putCollaborationApiReopenCollaborationThread, deleteCollaborationApiDeleteCollaborationThreadSoft } =
    useRestClient()

  const { mutate: reopenThread } = putCollaborationApiReopenCollaborationThread.useMutation()
  const { mutate: deleteThreadSoft } = deleteCollaborationApiDeleteCollaborationThreadSoft.useMutation()

  const handleReopenThread = () => {
    if (!appId || !workspaceId || !channelId || !threadId) {
      toast.error('App ID, workspace ID, channel ID and thread ID are required to reopen a thread.')
      return
    }
    reopenThread(
      { path: { applicationId: appId, workspaceId, channelId, threadId } },
      {
        onSuccess: () => {
          toast.success('Thread reopened successfully')
          invalidateThreadAndLogs()
        },
        onError: () => {
          toast.error('Failed to reopen thread')
        },
      }
    )
  }

  const handleDeleteThread = () => {
    if (!appId || !workspaceId || !channelId || !threadId) {
      toast.error('App ID, workspace ID, channel ID and thread ID are required to delete a thread.')
      return
    }
    deleteThreadSoft(
      { path: { applicationId: appId, workspaceId, channelId, threadId } },
      {
        onSuccess: () => {
          toast.success('Thread deleted successfully')
          invalidateThreadAndLogs()
        },
      }
    )
  }

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <IconBg className="size-8 border border-green-500 bg-green-500/10">
          <IconCheckMarkOutline className="text-green-500" />
        </IconBg>
        <span>This thread has been resolved</span>
      </div>
      <div className="flex gap-2">
        <ConfirmDialog
          title="Reopen Thread"
          trigger={
            <Button variant="outline" size="sm">
              Reopen
            </Button>
          }
          description="This thread has been resolved. Are you sure you want to reopen it?"
          onConfirm={handleReopenThread}
          confirmLabel="Reopen"
        />
        <ConfirmDialog
          title="Delete Thread"
          trigger={
            <Button variant="destructive" size="sm">
              Delete
            </Button>
          }
          description="Are you sure you want to delete this thread?"
          onConfirm={handleDeleteThread}
          confirmLabel="Delete"
        />
      </div>
    </div>
  )
}
